import { DateTime } from 'luxon';
import type { Locales } from '@/i18n/i18n-types';
import type { RevenueChartResponseDto } from '@/generated/korza-api-client/types.gen';
import type { TenantRepurchaseRateChartResponseDto } from '@/generated/korza-api-client/types.gen';
import {
  bucketStartIso,
  compareIsoDateStrings,
  formatChartAxisLabel,
  type ChartGranularity,
} from '@/lib/chart-axis';

type SalesChartPointDto = RevenueChartResponseDto['points'][number];
type TenantRepurchaseRateChartPointDto =
  TenantRepurchaseRateChartResponseDto['points'][number];

function bucketKey(
  date: string,
  granularity: ChartGranularity,
  locale: Locales
): string | null {
  return (
    bucketStartIso(date, granularity, locale) ??
    (DateTime.fromISO(date).isValid ? date : null)
  );
}

export function mergePaidOrderCountsByBucket(
  points: SalesChartPointDto[],
  granularity: ChartGranularity,
  locale: Locales
): Map<string, number> {
  const merged = new Map<string, number>();
  for (const point of points) {
    const k = bucketKey(point.date, granularity, locale);
    if (!k) continue;
    merged.set(k, (merged.get(k) ?? 0) + point.count);
  }
  return merged;
}

/**
 * `date` is the ORIGINAL api date of the first point in the bucket (not the key
 * regrouped in the browser zone), so clicks can send the exact backend bucket
 * start (AD-011 / R1).
 */
export function buildPaidOrdersChartData(
  points: SalesChartPointDto[],
  granularity: ChartGranularity,
  locale: Locales
): Array<{ month: string; count: number; date: string }> {
  const merged = mergePaidOrderCountsByBucket(points, granularity, locale);
  const originalDates = new Map<string, string>();
  for (const point of points) {
    const k = bucketKey(point.date, granularity, locale);
    if (k && !originalDates.has(k)) originalDates.set(k, point.date);
  }
  return [...merged.entries()]
    .sort(([a], [b]) => compareIsoDateStrings(a, b))
    .map(([iso, count]) => ({
      month: formatChartAxisLabel(DateTime.fromISO(iso), granularity, locale),
      count,
      date: originalDates.get(iso) ?? iso,
    }));
}

/** Same headroom as the previous repurchase-only Y-axis: ceil(max)+1, floor 1. */
export function computeSharedCountAxisMax(
  salesPoints: SalesChartPointDto[],
  repurchasePoints: TenantRepurchaseRateChartPointDto[],
  granularity: ChartGranularity,
  locale: Locales
): number {
  const paid = mergePaidOrderCountsByBucket(salesPoints, granularity, locale);
  let max = 0;
  for (const c of paid.values()) {
    max = Math.max(max, c);
  }
  for (const p of repurchasePoints) {
    max = Math.max(max, p.inAppRepurchaseQuantity + p.channelMigrationQuantity);
  }
  return Math.max(1, Math.ceil(max) + 1);
}
